import CountrySelect from "./choices/CountrySelect";
import LeaguesSelect from "./choices/LeaguesSelect";
import BMSelect from "./choices/BMSelect";
import FlatpickrDate from "./FatpikrDate";
import MarketsItem from "./choices/MarketsItem";
import MarketsCheckbox from "./choices/MarketsCheckbox";
import { useGlobalStateStore } from "../stores/store";
import Odds from "./filterComponents/Odds";
import { loadNextOddsPage } from "./interactors/odds";

import OverImage from "../assets/img/over.svg";
import UnderImage from "../assets/img/under.svg";
import Winner from "./IconsSvg/Winner";
import DC from "./IconsSvg/DC";
import { useEffect, useState, useMemo } from "react";
import moment from "moment";

function FilterForm() {
  const store = useGlobalStateStore();
  const filter = useGlobalStateStore((state) => state.filter);
  const markets = useGlobalStateStore((state) => state.filter.markets);
  const { min, max, enabled } = useGlobalStateStore(
    (state) => state.filter.oddsValue
  );
  const accessToken = localStorage.getItem("token");

  const isOddsRangeInvalid = enabled && min >= max;

  const handleSubmit = (e) => {
    e.preventDefault();

    const exp = moment(localStorage.getItem("exp"));
    const now = moment();
    if (exp.isBefore(now)) {
      window.location.href = "/auth";
    }

    const overGoals = [];
    document
      .querySelectorAll(".switch__item.over_goals input")
      .forEach((input) => {
        if (input.checked) overGoals.push(input.value);
      });

    const underGoals = [];
    document
      .querySelectorAll(".switch__item.under_goals input")
      .forEach((input) => {
        if (input.checked) underGoals.push(input.value);
      });

    localStorage.setItem("overGoals", JSON.stringify(overGoals));
    localStorage.setItem("underGoals", JSON.stringify(underGoals));

    const activeTab = store.getActiveTab(); // ✅ Get current tab

    store.setMode("filter");
    store.setLastLoadedPage(activeTab, 0); // ✅ tab-specific page reset

    loadNextOddsPage(store, {
      tab: activeTab, // ✅ apply filter to correct market
      onBeforeSuccess: () => store.clearOddsData(),
      onSuccess: (itemsLoaded) => store.setCanLoadMore(itemsLoaded > 0),
    });

    document.querySelector("header")?.classList.remove("filter-open");
    document.querySelector("body")?.classList.remove("open");
  };

  const handleClearFilters = (e) => {
    e.preventDefault();

    store.resetFilter();
    localStorage.removeItem("overGoals");
    localStorage.removeItem("underGoals");

    document
      .querySelectorAll(".filter-form input[type='checkbox']")
      .forEach((checkbox) => {
        if (!checkbox.disabled) checkbox.checked = false;
      });

    document.querySelectorAll(".filter-form select").forEach((select) => {
      select.selectedIndex = 0;
    });

    const activeTab = store.getActiveTab(); // ✅ Get current tab

    store.setMode("filter");
    store.setLastLoadedPage(activeTab, 0);

    loadNextOddsPage(store, {
      tab: activeTab, // ✅ make sure it's applied to the right market
      onBeforeSuccess: () => store.clearOddsData(),
      onSuccess: (itemsLoaded) => store.setCanLoadMore(itemsLoaded > 0),
    });

    document.querySelector("header")?.classList.remove("filter-open");
    document.querySelector("body")?.classList.remove("open");
  };

  const isUserLoggedIn = () => !!accessToken;

  useEffect(() => {
    const checkboxes = document.querySelectorAll(
      ".filter-form .custom-checkbox:not(.winner)"
    );

    checkboxes.forEach((customCheckbox) => {
      const checkboxInput = customCheckbox.querySelector(
        'input[type="checkbox"]'
      );

      if (accessToken) {
        checkboxInput.disabled = false;
        return;
      }

      const handleClick = (e) => {
        const token = localStorage.getItem("token");
        if (!token) {
          e.preventDefault();
          showPopover(customCheckbox);
        } else {
          checkboxInput.disabled = false;
          checkboxInput.checked = !checkboxInput.checked;
        }
      };

      const showPopover = (target) => {
        const popover = target.querySelector(".popover");
        if (popover) {
          popover.classList.add("show");
          setTimeout(() => popover.classList.remove("show"), 3000);
        }
      };

      customCheckbox.addEventListener("click", handleClick);

      const popoverLink = customCheckbox.querySelector(".popover a");
      if (popoverLink) {
        popoverLink.addEventListener("click", (e) => e.stopPropagation());
      }

      return () => {
        customCheckbox.removeEventListener("click", handleClick);
        if (popoverLink) {
          popoverLink.removeEventListener("click", (e) => e.stopPropagation());
        }
      };
    });
  }, [accessToken]);

  const isMarketFilterSelected = (markets) => {
    return Object.values(markets).some((value) => {
      return Array.isArray(value) ? value.length > 0 : Boolean(value);
    });
  };

  const isButtonDisabled = useMemo(
    () => !isMarketFilterSelected(markets) || isOddsRangeInvalid,
    [markets, isOddsRangeInvalid]
  );

  return (
    <form onSubmit={handleSubmit} className="filter-form">
      <FlatpickrDate />
      <CountrySelect />
      <LeaguesSelect />
      <BMSelect />

      <h5>Markets</h5>
      <div className="markets__item__wrap">
        <MarketsCheckbox
          image={<Winner />}
          title="Winner"
          key={"checkboxwinner"}
          name="checkBoxWinner"
          className="winner"
          inputdisabled={false}
        />
        <MarketsCheckbox
          image={<DC />}
          title="Double Chance"
          key={"checkBoxDc"}
          name="checkBoxDc"
          className="double_chance"
          inputdisabled={!isUserLoggedIn()}
        />
        <MarketsItem
          name="over_goals"
          image={OverImage}
          title="Over Goals"
          values={["0.5", "1.5", "2.5", "3.5", "4.5", "5.5"]}
          selectedValues={filter.markets.over_goals ?? []}
          inputdisabled={!isUserLoggedIn()}
        />
        <MarketsItem
          name="under_goals"
          image={UnderImage}
          title="Under Goals"
          values={["0.5", "1.5", "2.5", "3.5", "4.5", "5.5"]}
          selectedValues={filter.markets.under_goals ?? []}
          inputdisabled={!isUserLoggedIn()}
        />
      </div>

      <h5>Odds Value</h5>
      <Odds />

      <button
        onClick={handleSubmit}
        className="main-btn"
        disabled={isButtonDisabled}
        title={isButtonDisabled ? "Min value must be less than Max value" : ""}
      >
        Apply
      </button>
      <button onClick={handleClearFilters} className="clear-btn">
        Clear filters
      </button>
    </form>
  );
}

export default FilterForm;
